import type { ChartDataPoint, LineConfig } from "../../hooks/useD3LineChart";
import {
  ACTUAL_COMPARISON_ACTUAL_KEY,
  ACTUAL_COMPARISON_ALL_KEY,
  ACTUAL_COMPARISON_STRATEGY_KEY,
} from "../../hooks/useStrategyVsActualComparison";
import type { FactorEntry } from "../../services/productFactorsService";
import {
  getBucketMsForFilter,
  getLocalTodayStart,
  getV3PeriodStart,
  V3_FACTOR_SLOT_MS,
  V3_INVESTOR_TIME_FILTER_OPTIONS,
  type V3TimeFilter,
} from "../../components/performanceChartV3Utils";

const DAY_MS = 24 * 60 * 60 * 1000;

export const ALPHA_PERFORMANCE_TIME_FILTER_OPTIONS =
  V3_INVESTOR_TIME_FILTER_OPTIONS.filter((option) => option.value !== "All");

interface AlphaFactorWindow {
  startMs: number;
  endMs: number;
  includeStart: boolean;
}

export interface AlphaPerformancePeriodResult {
  strategyFactors: FactorEntry[];
  actualFactors: FactorEntry[];
  strategyReturn: number | null;
  actualReturn: number | null;
  strategyWindow: AlphaFactorWindow | null;
  actualWindow: AlphaFactorWindow | null;
}

export interface AlphaPerformanceContext {
  sharedStartDayMs: number | null;
  sharedAnchorMs: number | null;
  strategyFactorsFromSharedStart: FactorEntry[];
  actualFactorsFromSharedStart: FactorEntry[];
  periods: Record<string, AlphaPerformancePeriodResult>;
  strategyTimeframePerformance: Record<string, number | null>;
  actualTimeframePerformance: Record<string, number | null>;
  availableFilters: V3TimeFilter[];
  actualSummaryFilters: V3TimeFilter[];
}

function parseFactorValue(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function normalizeAlphaPerformanceFactors(
  factors: ReadonlyArray<{ t: string; f: unknown }>
): FactorEntry[] {
  return factors
    .map((factor) => {
      const timestampMs = new Date(factor.t).getTime();
      const value = parseFactorValue(factor.f);
      if (Number.isNaN(timestampMs) || value === null) return null;
      return { t: factor.t, f: value, timestampMs };
    })
    .filter((factor): factor is FactorEntry & { timestampMs: number } => factor !== null)
    .sort((left, right) => left.timestampMs - right.timestampMs)
    .map(({ t, f }) => ({ t, f }));
}

export function hasDuplicateAlphaPerformanceDates(
  factors: ReadonlyArray<{ t: string }>
): boolean {
  const seenDates = new Set<string>();
  for (const factor of factors) {
    const timestampMs = new Date(factor.t).getTime();
    if (Number.isNaN(timestampMs)) continue;
    const utcDate = new Date(timestampMs).toISOString().slice(0, 10);
    if (seenDates.has(utcDate)) return true;
    seenDates.add(utcDate);
  }
  return false;
}

function getUtcDayStartMsFromMs(timestampMs: number): number {
  const date = new Date(timestampMs);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function getUtcDayStartMs(timestamp: string): number {
  return getUtcDayStartMsFromMs(new Date(timestamp).getTime());
}

function getLatestTimestampMs(factors: FactorEntry[]): number | null {
  if (factors.length === 0) return null;
  return new Date(factors[factors.length - 1].t).getTime();
}

function getPeriodWindow(
  period: V3TimeFilter,
  sharedAnchorMs: number | null,
  inceptionBaselineMs: number | null,
  nowMs: number,
  todayMode: "strategy-local" | "actual-utc-inclusive"
): AlphaFactorWindow | null {
  if (sharedAnchorMs === null || inceptionBaselineMs === null) return null;

  if (period === "Today") {
    const now = new Date(nowMs);
    const startMs = todayMode === "actual-utc-inclusive"
      ? Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
      : getLocalTodayStart(now).getTime();
    return {
      startMs,
      endMs: sharedAnchorMs,
      includeStart: todayMode === "actual-utc-inclusive",
    };
  }

  if (period === "All" || period === "Since inception") {
    return {
      startMs: inceptionBaselineMs,
      endMs: sharedAnchorMs,
      includeStart: false,
    };
  }

  const start = getV3PeriodStart(period, new Date(sharedAnchorMs));
  if (!start) return null;
  return {
    startMs: start.getTime(),
    endMs: sharedAnchorMs,
    includeStart: false,
  };
}

function sliceFactors(
  factors: FactorEntry[],
  window: AlphaFactorWindow | null
): FactorEntry[] {
  if (!window || window.startMs > window.endMs) return [];
  return factors.filter((factor) => {
    const timestampMs = new Date(factor.t).getTime();
    if (timestampMs > window.endMs) return false;
    return window.includeStart
      ? timestampMs >= window.startMs
      : timestampMs > window.startMs;
  });
}

export function compoundAlphaPerformanceFactors(
  factors: ReadonlyArray<FactorEntry>
): number | null {
  if (factors.length === 0) return null;
  return (factors.reduce((product, factor) => product * factor.f, 1) - 1) * 100;
}

export function buildAlphaPerformanceContext({
  strategyFactors,
  actualFactors,
  nowMs,
}: {
  strategyFactors: ReadonlyArray<{ t: string; f: unknown }>;
  actualFactors: ReadonlyArray<{ t: string; f: unknown }>;
  nowMs: number;
}): AlphaPerformanceContext {
  const normalizedStrategy = normalizeAlphaPerformanceFactors(strategyFactors);
  const normalizedActual = normalizeAlphaPerformanceFactors(actualFactors);

  const strategyStartDayMs = normalizedStrategy.length > 0
    ? getUtcDayStartMs(normalizedStrategy[0].t)
    : null;
  const actualStartDayMs = normalizedActual.length > 0
    ? getUtcDayStartMs(normalizedActual[0].t)
    : null;
  const sharedStartDayMs = strategyStartDayMs !== null && actualStartDayMs !== null
    ? Math.max(strategyStartDayMs, actualStartDayMs)
    : strategyStartDayMs ?? actualStartDayMs;

  const strategyFactorsFromSharedStart = sharedStartDayMs === null
    ? []
    : normalizedStrategy.filter(
      (factor) => new Date(factor.t).getTime() >= sharedStartDayMs
    );
  const actualFactorsFromSharedStart = sharedStartDayMs === null
    ? []
    : normalizedActual.filter(
      (factor) => new Date(factor.t).getTime() >= sharedStartDayMs
    );

  const sourceAnchors = [
    getLatestTimestampMs(strategyFactorsFromSharedStart),
    getLatestTimestampMs(actualFactorsFromSharedStart),
  ].filter((value): value is number => value !== null);
  const sharedAnchorMs = sourceAnchors.length > 0 ? Math.max(...sourceAnchors) : null;
  const inceptionBaselineMs = sharedStartDayMs === null
    ? null
    : sharedStartDayMs - DAY_MS;

  const periods: Record<string, AlphaPerformancePeriodResult> = {};
  const strategyTimeframePerformance: Record<string, number | null> = {};
  const actualTimeframePerformance: Record<string, number | null> = {};
  const availableFilters: V3TimeFilter[] = [];
  const actualSummaryFilters: V3TimeFilter[] = [];

  V3_INVESTOR_TIME_FILTER_OPTIONS.forEach((option) => {
    const strategyWindow = getPeriodWindow(
      option.value,
      sharedAnchorMs,
      inceptionBaselineMs,
      nowMs,
      "strategy-local"
    );
    const actualWindow = getPeriodWindow(
      option.value,
      sharedAnchorMs,
      inceptionBaselineMs,
      nowMs,
      "actual-utc-inclusive"
    );
    const strategySlice = sliceFactors(strategyFactorsFromSharedStart, strategyWindow);
    const actualSlice = sliceFactors(actualFactorsFromSharedStart, actualWindow);
    const strategyReturn = compoundAlphaPerformanceFactors(strategySlice);
    const actualReturn = compoundAlphaPerformanceFactors(actualSlice);

    periods[option.value] = {
      strategyFactors: strategySlice,
      actualFactors: actualSlice,
      strategyReturn,
      actualReturn,
      strategyWindow,
      actualWindow,
    };
    strategyTimeframePerformance[option.value] = strategyReturn;
    actualTimeframePerformance[option.value] = actualReturn;

    if (option.value !== "All") {
      if (strategySlice.length > 0 || actualSlice.length > 0) {
        availableFilters.push(option.value);
      }
      if (actualSlice.length > 0) {
        actualSummaryFilters.push(option.value);
      }
    }
  });

  return {
    sharedStartDayMs,
    sharedAnchorMs,
    strategyFactorsFromSharedStart,
    actualFactorsFromSharedStart,
    periods,
    strategyTimeframePerformance,
    actualTimeframePerformance,
    availableFilters,
    actualSummaryFilters,
  };
}

interface AlphaChartSeries {
  id: string;
  label: string;
  color: string;
  factors: FactorEntry[];
  window: AlphaFactorWindow;
}

function getDisplayBaselineMs(window: AlphaFactorWindow): number {
  // Actual Today includes its UTC-midnight row. Keep the established one-slot
  // synthetic reference immediately before that inclusive factor so the chart
  // can show both 0% and the first real cumulative value.
  return window.includeStart
    ? window.startMs - V3_FACTOR_SLOT_MS
    : window.startMs;
}

/**
 * Periods whose display bucket is coarser than the 30-minute factor grid render
 * on a UTC-day grid. Actual is a UTC-date daily series stamped at each day's
 * UTC midnight, and the Strategy feed carries a slot at every half hour, so a
 * calendar-day bucket's last real slot is the next day's midnight; stamping the
 * bucket there drew every Strategy day one day late against Actual. Placing
 * both lines on the same UTC-day grid puts each day's return at that day's
 * midnight, gives the two lines identical timestamps, and matches the legacy
 * daily overlay. Membership is untouched: the canonical period slice is fixed
 * before this bucketing, so every endpoint still equals its return.
 */
function usesUtcDailyDisplay(period: V3TimeFilter): boolean {
  return getBucketMsForFilter(period) > V3_FACTOR_SLOT_MS;
}

/**
 * A daily bucket sits at its UTC midnight. A rolling window (1W, 1M, 3M, 1Y)
 * can start inside a day; that partial first day keeps its last real slot so
 * the point stays after the 0% baseline instead of colliding with or preceding
 * it, and its return is drawn where it finished accruing rather than as a jump
 * at the window's left edge.
 */
function getDailyDisplayTimestampMs(
  dayStartMs: number,
  lastRealTimestampMs: number,
  baselineMs: number
): number {
  return dayStartMs > baselineMs ? dayStartMs : lastRealTimestampMs;
}

/**
 * The value-less domain end row follows the same display grid as the lines: on
 * UTC-daily periods the anchor day's point sits at that day's midnight, so the
 * axis ends there rather than at the raw anchor slot. When that midnight is not
 * after the domain start (a window that opens on the anchor's own day, such as
 * YTD on January 1) the anchor day's point carries its last real slot instead,
 * so the axis ends on the raw anchor.
 */
function getDomainEndMs(
  sharedAnchorMs: number | null,
  domainStartMs: number | null,
  period: V3TimeFilter
): number | null {
  if (sharedAnchorMs === null) return null;
  if (!usesUtcDailyDisplay(period) || domainStartMs === null) return sharedAnchorMs;
  const anchorDayStartMs = getUtcDayStartMsFromMs(sharedAnchorMs);
  return anchorDayStartMs > domainStartMs ? anchorDayStartMs : sharedAnchorMs;
}

function buildSeriesPoints(
  series: AlphaChartSeries,
  period: V3TimeFilter
): ChartDataPoint[] {
  if (series.factors.length === 0) return [];

  const baselineMs = getDisplayBaselineMs(series.window);
  const dailyDisplay = usesUtcDailyDisplay(period);
  const bucketMs = getBucketMsForFilter(period);
  const buckets = new Map<number, { factor: number; lastRealTimestampMs: number }>();

  series.factors.forEach((factor) => {
    const timestampMs = new Date(factor.t).getTime();
    // Daily buckets key on the factor's own UTC day so a day's slots collapse
    // onto that day. Intraday buckets keep the baseline-relative grid.
    const bucketKey = dailyDisplay
      ? getUtcDayStartMsFromMs(timestampMs)
      : Math.max(1, Math.ceil((timestampMs - baselineMs) / bucketMs));
    const current = buckets.get(bucketKey);
    buckets.set(bucketKey, {
      factor: (current?.factor ?? 1) * factor.f,
      lastRealTimestampMs: Math.max(current?.lastRealTimestampMs ?? timestampMs, timestampMs),
    });
  });

  const bucketKeys = [...buckets.keys()].sort((left, right) => left - right);
  if (bucketKeys.length === 0) return [];

  const points: ChartDataPoint[] = [{
    date: new Date(baselineMs).toISOString(),
    fullDate: new Date(baselineMs).toISOString(),
    [series.id]: 0,
  }];
  let cumulative = 1;
  bucketKeys.forEach((bucketKey) => {
    const bucket = buckets.get(bucketKey);
    if (!bucket) return;
    cumulative *= bucket.factor;
    const timestampMs = dailyDisplay
      ? getDailyDisplayTimestampMs(bucketKey, bucket.lastRealTimestampMs, baselineMs)
      : bucket.lastRealTimestampMs;
    const timestamp = new Date(timestampMs).toISOString();
    points.push({
      date: timestamp,
      fullDate: timestamp,
      [series.id]: (cumulative - 1) * 100,
    });
  });

  return points;
}

export function buildAlphaPerformanceChart({
  context,
  period,
  selectedKey,
  strategyColor,
  actualColor,
}: {
  context: AlphaPerformanceContext;
  period: V3TimeFilter;
  selectedKey: string;
  strategyColor: string;
  actualColor: string;
}): { chartData: ChartDataPoint[]; lines: LineConfig[] } {
  const periodResult = context.periods[period];
  if (!periodResult) return { chartData: [], lines: [] };

  const series: AlphaChartSeries[] = [];
  const includeStrategy = selectedKey === ACTUAL_COMPARISON_ALL_KEY
    || selectedKey === ACTUAL_COMPARISON_STRATEGY_KEY;
  const includeActual = selectedKey === ACTUAL_COMPARISON_ALL_KEY
    || selectedKey === ACTUAL_COMPARISON_ACTUAL_KEY;

  if (
    includeStrategy
    && periodResult.strategyFactors.length > 0
    && periodResult.strategyWindow
  ) {
    series.push({
      id: ACTUAL_COMPARISON_STRATEGY_KEY,
      label: "Strategy",
      color: strategyColor,
      factors: periodResult.strategyFactors,
      window: periodResult.strategyWindow,
    });
  }
  if (
    includeActual
    && periodResult.actualFactors.length > 0
    && periodResult.actualWindow
  ) {
    series.push({
      id: ACTUAL_COMPARISON_ACTUAL_KEY,
      label: "Actual",
      color: actualColor,
      factors: periodResult.actualFactors,
      window: periodResult.actualWindow,
    });
  }

  const byTimestamp = new Map<string, ChartDataPoint>();
  const sourceWindows = [
    context.strategyFactorsFromSharedStart.length > 0
      ? periodResult.strategyWindow
      : null,
    context.actualFactorsFromSharedStart.length > 0
      ? periodResult.actualWindow
      : null,
  ].filter((window): window is AlphaFactorWindow => window !== null);
  const domainStartMs = sourceWindows.length > 0
    ? Math.min(...sourceWindows.map(getDisplayBaselineMs))
    : null;
  const domainEndMs = getDomainEndMs(context.sharedAnchorMs, domainStartMs, period);
  if (
    domainStartMs !== null
    && domainEndMs !== null
    && domainStartMs <= domainEndMs
  ) {
    [domainStartMs, domainEndMs].forEach((timestampMs) => {
      const timestamp = new Date(timestampMs).toISOString();
      byTimestamp.set(timestamp, { date: timestamp, fullDate: timestamp });
    });
  }
  series.forEach((item) => {
    buildSeriesPoints(item, period).forEach((point) => {
      const timestamp = point.fullDate ?? point.date;
      const current = byTimestamp.get(timestamp) ?? {
        date: timestamp,
        fullDate: timestamp,
      };
      current[item.id] = point[item.id];
      byTimestamp.set(timestamp, current);
    });
  });

  const chartData = [...byTimestamp.values()].sort(
    (left, right) => new Date(left.fullDate ?? left.date).getTime()
      - new Date(right.fullDate ?? right.date).getTime()
  );
  const lines: LineConfig[] = series.map((item) => ({
    dataKey: item.id,
    stroke: item.color,
    strokeWidth: item.id === ACTUAL_COMPARISON_STRATEGY_KEY ? 2.5 : 1.8,
    label: item.label,
    showDots: false,
  }));

  return { chartData, lines };
}
